import { useState } from "react"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../../../../localization/useDateTime"
import { useSingleActiveEditItem } from "../../../../../../../shared/components/singleActiveEdit/SingleActiveEdit"
import { MeetingServiceOrder, MeetingServiceOrdersView } from "../../meetingServiceTypes"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { getTimeToStart } from "../helpers"
import { SavedOrderInfo, useProductionOrderEdit } from "../useProductionOrderEdit"
import { ProductionOrderCardHeader } from "./ProductionOrderCardHeader"
import { ProductionOrderItemsSection } from "./ProductionOrderItemsSection"
import { ProductionOrderCardOverlays } from "./ProductionOrderCardOverlays"

const useStyles = makeStyles()((theme) => ({
    card: {
        borderRadius: ".625rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
        overflow: "hidden",
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    onEdit: (order: MeetingServiceOrder) => void
    onDelete: (order: MeetingServiceOrder) => void
    onSaved: (info: SavedOrderInfo) => void
    isSelected: boolean
    onToggleSelect: () => void
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

export function ProductionOrderCard({
    order,
    onEdit,
    onDelete,
    onSaved,
    isSelected,
    onToggleSelect,
    meetingServiceDependencies,
}: Props) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const timeToStart = getTimeToStart(order, dateTimeFactory, dateTimeFactory.now())

    const [showReceipt, setShowReceipt] = useState(false)
    const [showChat, setShowChat] = useState(false)
    const [showPackingSlip, setShowPackingSlip] = useState(false)

    const edit = useProductionOrderEdit({
        order,
        onRequestDelete: () => onDelete(order),
        // The saved-dialog lives on the page (not the card) so it survives this card unmounting when the
        // edited order drops out of the filtered list after the post-save refetch.
        onSaved,
    })

    // Only one order can be inline-edited at a time; starting/leaving an edit is guarded for unsaved changes.
    const { rootRef: cardRef, requestEdit } = useSingleActiveEditItem<HTMLDivElement>(order.id, {
        hasChanges: edit.hasChanges && !edit.isSaving,
        cancel: edit.cancelEdit,
    })

    return (
        <>
            <Box ref={cardRef} className={classes.card}>
                <ProductionOrderCardHeader
                    order={order}
                    isSelected={isSelected}
                    onToggleSelect={onToggleSelect}
                    view={meetingServiceDependencies.view ?? MeetingServiceOrdersView.PRODUCTION}
                    meetingServiceDependencies={meetingServiceDependencies}
                    timeToStart={timeToStart}
                    onOpenChat={() => setShowChat(true)}
                    onShowReceipt={() => setShowReceipt(true)}
                    onShowPackingSlip={() => setShowPackingSlip(true)}
                    onFullEdit={() => onEdit(order)}
                    onDelete={() => onDelete(order)}
                />

                <ProductionOrderItemsSection
                    order={order}
                    edit={edit}
                    onStartEdit={(deliveryKey, deliveryTime) =>
                        requestEdit(() => edit.startEdit(deliveryKey, deliveryTime))
                    }
                />
            </Box>

            <ProductionOrderCardOverlays
                order={order}
                showReceipt={showReceipt}
                onCloseReceipt={() => setShowReceipt(false)}
                showChat={showChat}
                onCloseChat={() => setShowChat(false)}
                showPackingSlip={showPackingSlip}
                onClosePackingSlip={() => setShowPackingSlip(false)}
            />
        </>
    )
}
